import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, FileText, Download } from 'lucide-react';
import { fmtUSD, AR_STATUS_STYLES, SERVICE_LABELS, todayISO } from './utils';
import { API_BASE } from '../../utils/apiBase';
import logger from '../../utils/logger';

const EMPTY = { payer_name: '', service_line: 'day_program', invoice_number: '', invoice_date: todayISO(), due_date: '', period_start: '', period_end: '', amount: '', paid_amount: '', paid_date: '', status: 'submitted', notes: '' };

export default function ARTab({ api, onRefresh }) {
  const [list, setList] = useState([]);
  const [config, setConfig] = useState({ ar_payers: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState('all');

  const fetchAll = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([api.get('/finance/ar'), api.get('/finance/config')]);
      setList(l.data); setConfig(c.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (inv) => { setEditing(inv); setForm({ ...EMPTY, ...inv }); setDialog(true); };

  const save = async () => {
    try {
      if (!form.payer_name) { toast.error('Payer required'); return; }
      if (!form.amount) { toast.error('Amount required'); return; }
      if (editing) { await api.put(`/finance/ar/${editing.id}`, form); toast.success('Invoice updated'); }
      else { await api.post('/finance/ar', form); toast.success('Invoice added'); }
      setDialog(false); fetchAll(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this invoice?')) return;
    await api.delete(`/finance/ar/${id}`); fetchAll(); onRefresh?.();
  };

  const filtered = list.filter(i => filter === 'all' || i.status === filter || (filter === 'outstanding' && ['submitted', 'pending', 'partial'].includes(i.status)));
  const totals = {
    outstanding: list.filter(i => ['submitted', 'pending', 'partial'].includes(i.status)).reduce((s, i) => s + Math.max(0, (i.amount || 0) - (i.paid_amount || 0)), 0),
    over90: list.filter(i => i.aging_bucket === '90+').reduce((s, i) => s + (i.amount || 0), 0),
    paid_mtd: list.filter(i => i.status === 'paid' && (i.paid_date || '') >= todayISO().slice(0, 7)).reduce((s, i) => s + (i.paid_amount || 0), 0),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-3">
          <Card className="border-[#64B5F6] bg-[#E3F2FD]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1565C0] font-semibold">Outstanding</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.outstanding)}</p></CardContent></Card>
          <Card className="border-red-300 bg-red-50"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#D9534F] font-semibold">90+ Days</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.over90)}</p></CardContent></Card>
          <Card className="border-[#81C784] bg-[#E8F5E9]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#1B5E20] font-semibold">Paid MTD</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(totals.paid_mtd)}</p></CardContent></Card>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/api/finance/export/ar`, '_blank')} className="border-[#DED8EB] gap-1 h-9" data-testid="ar-export-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="ar-add-btn"><Plus className="h-4 w-4" /> Add Invoice</Button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['all','All'],['outstanding','Outstanding'],['submitted','Submitted'],['pending','Pending'],['paid','Paid'],['denied','Denied']].map(([k, l]) => (
          <Button key={k} size="sm" variant={filter === k ? 'default' : 'outline'} onClick={() => setFilter(k)}
            className={filter === k ? 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white h-8' : 'border-[#DED8EB] h-8'}
            data-testid={`ar-filter-${k}`}>{l}</Button>
        ))}
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : filtered.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No invoices.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="ar-list">
          {filtered.map(inv => (
            <Card key={inv.id} className="border shadow-sm bg-white" data-testid={`ar-row-${inv.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{inv.payer_name}</h3>
                      <Badge className={`text-[10px] border ${AR_STATUS_STYLES[inv.status]}`}>{inv.status}</Badge>
                      {inv.service_line && <Badge className="text-[10px] bg-[#F0EDF6] text-[#6B3FA0] border-[#DED8EB]">{SERVICE_LABELS[inv.service_line] || inv.service_line}</Badge>}
                      {inv.aging_bucket && inv.aging_bucket !== 'paid' && inv.status !== 'paid' && (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${inv.aging_bucket === '90+' ? 'bg-red-100 text-[#D9534F]' : inv.aging_bucket === '61-90' ? 'bg-orange-100 text-[#E65100]' : 'bg-[#F0EDF6] text-[#8E85A0]'}`}>
                          {inv.aging_bucket} days
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#5A4D6E] mt-0.5">
                      Invoice {inv.invoice_number || '—'} · {inv.invoice_date}{inv.period_start && inv.period_end ? ` · Period ${inv.period_start} → ${inv.period_end}` : ''}
                    </p>
                    {inv.notes && <p className="text-[11px] text-[#8E85A0] mt-0.5">{inv.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(inv.amount)}</p>
                      {inv.paid_amount > 0 && <p className="text-[10px] text-[#1B5E20]">Paid {fmtUSD(inv.paid_amount)}</p>}
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(inv)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`ar-edit-${inv.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(inv.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`ar-del-${inv.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="ar-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Invoice' : 'New Invoice'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Payer *</Label>
              <Select value={form.payer_name} onValueChange={v => setForm(f => ({ ...f, payer_name: v }))}>
                <SelectTrigger className="border-[#DED8EB]" data-testid="ar-payer-select"><SelectValue placeholder="Select payer" /></SelectTrigger>
                <SelectContent>{(config.ar_payers || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Or type custom payer" value={form.payer_name} onChange={e => setForm(f => ({ ...f, payer_name: e.target.value }))} className="border-[#DED8EB] mt-1" data-testid="ar-payer-text" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Service Line</Label>
                <Select value={form.service_line} onValueChange={v => setForm(f => ({ ...f, service_line: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="ar-service"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(SERVICE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="ar-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{['submitted', 'pending', 'partial', 'paid', 'denied'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Invoice #</Label><Input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-inv-num" /></div>
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-amount" /></div>
              <div><Label>Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-inv-date" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-due-date" /></div>
              <div><Label>Period Start</Label><Input type="date" value={form.period_start} onChange={e => setForm(f => ({ ...f, period_start: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-period-start" /></div>
              <div><Label>Period End</Label><Input type="date" value={form.period_end} onChange={e => setForm(f => ({ ...f, period_end: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-period-end" /></div>
              <div><Label>Paid Amount</Label><Input type="number" step="0.01" value={form.paid_amount} onChange={e => setForm(f => ({ ...f, paid_amount: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-paid-amount" /></div>
              <div><Label>Paid Date</Label><Input type="date" value={form.paid_date} onChange={e => setForm(f => ({ ...f, paid_date: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-paid-date" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="border-[#DED8EB]" data-testid="ar-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="ar-save-btn">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
